import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import type { FieldValue } from 'tio-common/utils/tuition-assistance/fields';
import type { TASApplicationModelFieldsSignature } from 'tio-data/data/resources/tas-application.type';
import type { TasApplicationSubmissionFieldValues } from 'tio-data/requests/tas-application-actions';

/**
 * The guided wizard's submit pipelines, as data + dependency-injected runners.
 * The ordering is the contract: local and fresh limit gates run before any
 * write, the course-approval path stages its final values before issuing one
 * atomic request, and the persisted result is verified before anything is
 * presented as submitted.
 */

/** The application fields the submit-time save stamps with money amounts. */
export type TotalsField =
  | 'PRE_APPROVED_TOTAL'
  | 'FINAL_APPROVED_TOTAL'
  | 'CARD_PRE_APPROVED_TOTAL'
  | 'CARD_FINAL_APPROVED_TOTAL';

export interface TotalsExpectation {
  field: TotalsField;
  /** Cents the submit-time save stamped; null when the save cleared the field. */
  expected: number | null;
}

/**
 * What the submit-time save is expected to have persisted, mirroring the
 * wizard's stamping logic: a CourseCa$h direct-pay submit splits the request
 * across a card field and a non-card field, every other submit writes the
 * whole amount to one field and clears both card fields. Type 2 programs use
 * the FINAL_* fields, everything else the PRE_* fields.
 */
export function buildTotalsExpectations(params: {
  /** True for a direct-pay program paying via CourseCa$h. */
  usesCourseCashSplit: boolean;
  isTypeTwoProgram: boolean;
  /** Cents routed to the CourseCa$h card (already net of scholarships). */
  courseCashSubtotal: number;
  /** Cents paid outside the card. */
  nonCourseCashSubtotal: number;
  /** Cents for the whole request when no card split applies. */
  requestedTotalAmount: number;
}): TotalsExpectation[] {
  const totalField = params.isTypeTwoProgram ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';
  if (params.usesCourseCashSplit) {
    return [
      {
        field: params.isTypeTwoProgram ? 'CARD_FINAL_APPROVED_TOTAL' : 'CARD_PRE_APPROVED_TOTAL',
        expected: params.courseCashSubtotal,
      },
      { field: totalField, expected: params.nonCourseCashSubtotal },
    ];
  }
  return [
    { field: 'CARD_PRE_APPROVED_TOTAL', expected: null },
    { field: 'CARD_FINAL_APPROVED_TOTAL', expected: null },
    { field: totalField, expected: params.requestedTotalAmount },
  ];
}

/**
 * Compares the totals actually on a committed fields hash against what the
 * save was expected to stamp, returning the first expectation that does not
 * hold (or undefined when the write landed). A save request that silently
 * omits `fields` gets a 200 whose response body carries the old totals, so the
 * committed record — not the local editing copy — is what must be checked.
 * An expected amount of 0 is a real value: a missing/null persisted field does
 * not satisfy it.
 */
export function findPersistedTotalsMismatch(
  fields: TASApplicationModelFieldsSignature | undefined,
  expectations: TotalsExpectation[]
): TotalsExpectation | undefined {
  return expectations.find(({ field, expected }) => {
    const persisted = getSingleValueForTasField<
      TASApplicationModelFieldsSignature,
      FieldValue | undefined
    >(field, fields);
    const isEmpty = persisted === null || persisted === undefined || persisted === '';
    if (expected === null) {
      return !isEmpty;
    }
    return isEmpty || Number(persisted) !== expected;
  });
}

/** One field an atomic submission asked the server to persist; null means cleared. */
export interface SubmissionFieldExpectation {
  field: keyof TasApplicationSubmissionFieldValues;
  expected: number | string | null;
}

/**
 * Compares a committed fields hash against every value an atomic submission
 * carried, returning the first that did not land (or undefined when the write
 * landed). A server that accepts the transition but does not persist the
 * `submission` payload leaves these fields stale, so the reloaded record — not
 * the local editing copy — is what must be checked. Same semantics as
 * `findPersistedTotalsMismatch`: an expected null requires an empty persisted
 * value, and an expected 0 is a real amount a missing field does not satisfy.
 */
export function findPersistedSubmissionMismatch(
  fields: TASApplicationModelFieldsSignature | undefined,
  fieldValues: TasApplicationSubmissionFieldValues
): SubmissionFieldExpectation | undefined {
  const expectations = Object.entries(fieldValues) as [
    keyof TasApplicationSubmissionFieldValues,
    number | string | null | undefined,
  ][];
  for (const [field, expected] of expectations) {
    if (expected === undefined) {
      continue;
    }
    const persisted = getSingleValueForTasField<
      TASApplicationModelFieldsSignature,
      FieldValue | undefined
    >(field, fields);
    const isEmpty = persisted === null || persisted === undefined || persisted === '';
    const landed =
      expected === null
        ? isEmpty
        : typeof expected === 'number'
          ? !isEmpty && Number(persisted) === expected
          : String(persisted ?? '') === expected;
    if (!landed) {
      return { field, expected };
    }
  }
  return undefined;
}

export type GuidedSubmitOutcome =
  'blocked' | 'preflight-blocked' | 'totals-mismatch' | 'submitted' | 'errored';

/**
 * Runs the course-approval submit pipeline in its load-bearing order: local
 * gate, autosave drain, fresh limits preflight, local field staging, one
 * server request, then post-write verification. `drainPendingSaves` must not
 * resolve while any autosave write is still in flight or queued — otherwise a
 * slow autosave PATCH could land after the transition and overwrite what the
 * submission persisted — and must throw when the drained save failed, so the
 * pipeline aborts instead of proceeding over unknown state. The staged
 * submission is threaded from `stageSubmission` into both `submit` and
 * `findPersistedMismatch` so the request and the verification provably check
 * the same values; `submit` must leave the caller's record reloaded from the
 * server before it resolves. A mismatch means the server accepted the request
 * without persisting the submission — the transition may have occurred, so the
 * caller must surface a failure rather than a receipt. A false preflight is
 * expected to have surfaced its specific limit error; thrown errors route to
 * `onError`.
 */
export async function runGuidedSubmitFlow<Submission>(hooks: {
  canSubmit: () => boolean;
  drainPendingSaves: () => Promise<void>;
  preflight: () => Promise<boolean>;
  stageSubmission: () => Submission;
  submit: (submission: Submission) => Promise<unknown>;
  findPersistedMismatch: (submission: Submission) => SubmissionFieldExpectation | undefined;
  onBlocked: () => void;
  onPersistedMismatch: (mismatch: SubmissionFieldExpectation) => void;
  onError: (error: unknown) => void;
}): Promise<GuidedSubmitOutcome> {
  try {
    if (!hooks.canSubmit()) {
      hooks.onBlocked();
      return 'blocked';
    }
    await hooks.drainPendingSaves();
    if (!(await hooks.preflight())) {
      return 'preflight-blocked';
    }
    const submission = hooks.stageSubmission();
    await hooks.submit(submission);
    const mismatch = hooks.findPersistedMismatch(submission);
    if (mismatch) {
      hooks.onPersistedMismatch(mismatch);
      return 'totals-mismatch';
    }
    return 'submitted';
  } catch (error) {
    hooks.onError(error);
    return 'errored';
  }
}

/**
 * Runs the multi-request path for transition events that use the generic
 * transition endpoint, which does not accept the atomic `submission` payload.
 * `drainPendingSaves` carries the same contract as in `runGuidedSubmitFlow`,
 * and the fresh limits check still runs before totals or agreements are
 * persisted.
 */
export async function runGenericTransitionSubmitFlow(hooks: {
  canSubmit: () => boolean;
  drainPendingSaves: () => Promise<void>;
  preflight: () => Promise<boolean>;
  persistTotals: () => Promise<void>;
  findTotalsMismatch: () => TotalsExpectation | undefined;
  saveAgreements: () => Promise<unknown>;
  submit: () => Promise<unknown>;
  onBlocked: () => void;
  onTotalsMismatch: (mismatch: TotalsExpectation) => void;
  onError: (error: unknown) => void;
}): Promise<GuidedSubmitOutcome> {
  try {
    if (!hooks.canSubmit()) {
      hooks.onBlocked();
      return 'blocked';
    }
    await hooks.drainPendingSaves();
    if (!(await hooks.preflight())) {
      return 'preflight-blocked';
    }
    await hooks.persistTotals();
    const mismatch = hooks.findTotalsMismatch();
    if (mismatch) {
      hooks.onTotalsMismatch(mismatch);
      return 'totals-mismatch';
    }
    await hooks.saveAgreements();
    await hooks.submit();
    return 'submitted';
  } catch (error) {
    hooks.onError(error);
    return 'errored';
  }
}
